import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material';
import { useIntl } from 'react-intl';
import {
    colorBrandNeutral200,
    spacing40,
    spacing30,
    zIndexAppBar,
    zIndexDefault,
} from '@ellucian/react-design-system/core/styles/tokens';
import { makeStyles, useMediaQuery } from '@ellucian/react-design-system/core';
import { useHistory, useLocation, generatePath, matchPath } from 'react-router-dom';
import useApplicationForm from './useApplicationForm';
import { routes, CONSTANTS } from '../Helpers/Configuration';
import { findNextAvailablePageId, findPreviousAvailablePageId, isApplicationSubmitted } from '../Helpers/ApplicationHelpers';
import { useSnackbar } from '../SnackbarContext/SnackbarContext';
import { useSelector, useDispatch } from 'react-redux';
import { createDisableNavigationSelector, setPageAndCloseDialogAction } from './redux/actions/navigationActions';
import { startLoading, stopLoading } from '../../redux/shared/slices/isLoadingSlice';
import isInIframe from '../../utilities/isInIframe';
import useRoot from '../RootContext/useRoot';
import { useApi } from '../ApiContext';
import { FormattedMessage } from 'react-intl';
import { Typography, Button, SimpleDialog } from '@ellucian/react-design-system/core';
import { Route } from 'react-router-dom';
import SubmittedDialog from './SubmittedDialog';

const useStyles = makeStyles({
    name: 'ApplicationFormNavigationBar'
})(theme => ({
    root: {
        backgroundColor: colorBrandNeutral200,
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        padding: `${spacing40} 0`,
        zIndex: zIndexAppBar,
        [theme.breakpoints.up('md')]: {
            backgroundColor: 'unset',
            position: 'unset',
            gridTemplateColumns: '1fr',
            padding: 'unset',
            zIndex: zIndexDefault,
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: spacing40,
            '& button': {
                marginLeft: spacing30,
            },
        },
    },
    back: {
        display: 'flex',
        justifyContent: 'flex-start',
        paddingLeft: spacing40,
        [theme.breakpoints.up('md')]: {
            display: 'unset',
            justifyContent: 'unset',
            padding: 'unset',
        },
    },
    continue: {
        display: 'flex',
        justifyContent: 'flex-end',
        paddingRight: spacing40,
        [theme.breakpoints.up('md')]: {
            display: 'unset',
            justifyContent: 'unset',
            padding: 'unset',
        },
    },
}));

const selectApplication = state => state.application;
const selectAllValues = state => state.form.values;
const selectAllErrors = state => state.form.errors;
const selectApplicationIsSubmitted = state => isApplicationSubmitted(state.application);
const selectParentApplicationId = state => state.application.parentApplicationId;
const selectApplicationId = state => state.application.id;
const selectEnableSupplementalItems = state => state.template?.settings?.enableSupplementalItems && state.template?.supplementalItems?.items?.length && state.template?.supplementalItems?.items?.length > 0 ? true : false;
const selectApplicationPayment = state => state.application.paymentInformation;
const selectProspect = state => state.application.prospect;
const selectHiddenPageIds = state => state.settings.hiddenPageIds;
const selectApplicationIsPaymentPending = state => state.application?.paymentInformation?.paymentStatus === CONSTANTS.applicationPaymentInformation.paymentStatus.pending;

const ApplicationFormNavigationBar = props => {
    const intl = useIntl();
    const {
        onBack: onBackProp,
        onContinue: onContinueProp,
        onPageClick: onPageClickProp,
    } = props;
    const { classes } = useStyles();
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const {
        externalMode,
        getApplicationFee,
        getPaymentStatus,
        getRedirectUrl,
        globalSettings,
        importAdminMode,
        inquiryMode,
        previewMode,
        submit,
        supplementalFormMode,
        template,
        isAps,
    } = useApplicationForm();
    const rootCtx = useRoot();
    const disableNavigationSelector = useMemo(createDisableNavigationSelector, []);
    const disableNavigation = useSelector(
        state => disableNavigationSelector(state, location.pathname)
    );
    const api = useApi();
    const application = useSelector(selectApplication);
    const values = useSelector(selectAllValues);
    const errors = useSelector(selectAllErrors);
    const isSubmitted = useSelector(selectApplicationIsSubmitted);
    const parentApplicationId = useSelector(selectParentApplicationId);
    const applicationId = useSelector(selectApplicationId);
    const enableSupplementalItems = useSelector(selectEnableSupplementalItems);
    const applicationPayment = useSelector(selectApplicationPayment);
    const prospect = useSelector(selectProspect);
    const hiddenPageIds = useSelector(selectHiddenPageIds);
    const isPaymentPending = useSelector(selectApplicationIsPaymentPending);
    const theme = useTheme();
    const snackbar = useSnackbar();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isEmbedded = useMemo(() => isInIframe(), []);
    const [applicationFee, setApplicationFee] = useState(null);
    const [shouldPaymentShow, setShouldPaymentShow] = useState(false);
    const paymentStatus = CONSTANTS.applicationPaymentInformation.paymentStatus;
    const incompleteApplicationWarning = {
        message: intl.formatMessage({ id: 'incompleteApplicationTextId' }),
        ariaLabel: intl.formatMessage({ id: 'incompleteApplicationTextId' })
    };
    let continueButtonProps = {
        color: 'primary',
    };
    let backButtonProps = {
        color: 'secondary',
    };
    if (isMobile && !isEmbedded) {
        backButtonProps = { variant: 'text' };
        continueButtonProps = { variant: 'text', };
    }
    const [showSubmissionConfirmation, setShowSubmissionConfirmation] = useState(false);
    const [showSubmitted, setShowSubmitted] = useState(false);
    const [isSubmitting, setisSubmitting] = useState(false);
    const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
    const closeSubmissionConfirmation = useCallback(() => setShowSubmissionConfirmation(false));
    const closeSubmitted = useCallback(() => setShowSubmitted(false));
    const closePaymentConfirmation = useCallback(() => setShowPaymentConfirmation(false));
    const senderEmail = useMemo(() => template?.settings?.senderEmail, [template?.settings?.senderEmail]);

    const nextAvailablePageId = useMemo(() => {
        const applicationPage = matchPath(history.location.pathname, {
            path: [
                routes.adminTemplatePreviewPageById,
                routes.applicationFormPageById,
                routes.externalApplicationPageById,
                routes.importsTemplatePreviewPageById,
                routes.importsApplicationEditPageById
            ]
        });
        const pageId = applicationPage ? applicationPage.params.pageId : null;
        return findNextAvailablePageId(pageId, template?.pages, hiddenPageIds);
    }, [history?.location?.pathname, hiddenPageIds, template?.pages]);

    const previousAvailablePageId = useMemo(() => {
        const applicationPage = matchPath(history.location.pathname, {
            path: [
                routes.adminTemplatePreviewPageById,
                routes.applicationFormPageById,
                routes.externalApplicationPageById,
                routes.applicationFormReviewPage,
                routes.adminTemplatePreviewReviewPage,
                routes.externalApplicationwReviewPage,
                routes.importsTemplatePreviewPageById,
                routes.importsTemplatePreviewReviewPage,
                routes.importsApplicationEditPageById
            ]
        });
        if (!applicationPage) {
            return null;
        }
        const pageId = applicationPage ? applicationPage.params.pageId : null;
        return findPreviousAvailablePageId(pageId, template?.pages, hiddenPageIds);
    }, [history?.location?.pathname, hiddenPageIds, template?.pages]);

    useEffect(() => {
        const fn = async () => {
            try {
                const isOnReviewOrPayment = matchPath(history.location.pathname, {
                    path: [
                        routes.applicationFormReviewPage,
                        routes.adminTemplatePreviewReviewPage,
                        routes.externalApplicationwReviewPage,
                        routes.applicationFormPayments,
                        routes.externalApplicationPayments,
                        routes.importsTemplatePreviewReviewPage,
                    ]
                });
                if (isOnReviewOrPayment) {
                    dispatch(startLoading());
                    const result = await getApplicationFee(values);
                    setApplicationFee(result);
                    if (result) {
                        const showPayment = await api.applicationForms.checkForSubmittedPayment(application.id);
                        setShouldPaymentShow(showPayment);
                    }
                    dispatch(stopLoading());
                }
            } catch (e) {
                console.error(e);
                dispatch(stopLoading());
            }
        };
        fn();
    }, [values, history?.location?.pathname, setApplicationFee, dispatch]);

    const submitApplication = async () => {
        try {
            await submit(values);
            let route = generatePath(routes.applicationFormSubmitPage, { id: applicationId });
            if (previewMode) {
                route = generatePath(routes.importsTemplatePreviewSubmitPage, { id: applicationId });
            }
            else if (externalMode) {
                route = generatePath(routes.externalApplicationSubmitPage, { id: applicationId });
            }
            if (template.settings.enableSupplementalItems && template?.supplementalItems?.items?.length && template?.supplementalItems?.items?.length > 0) {
                route = generatePath(routes.applicationFormSupplementalItemsPage, { id: applicationId });
                if (previewMode) {
                    route = generatePath(routes.adminTemplatePreviewSupplementalItemsPage, { id: applicationId });
                }
                else if (externalMode) {
                    route = generatePath(routes.externalApplicationSupplementalItemsPage, { id: applicationId });
                }
            }
            history.push(route);
            setShowSubmitted(true);
        } catch (e) {
            console.error(e);
            snackbar.error(intl.formatMessage({ id: 'unableToSubmitApplication' }));
        }
    };

    const handleSubmissionConfirmationButtonClick = useCallback(async buttonId => {
        closeSubmissionConfirmation();
        if (buttonId === 'yes') {
            dispatch(startLoading());
            try {
                await submitApplication();
            } catch (error) {
                console.error(error);
                snackbar.error(intl.formatMessage({ id: 'unableToSubmitApplication' }));
            } finally {
                dispatch(stopLoading());
            }
        }
    }, [submitApplication, closeSubmissionConfirmation, dispatch, snackbar, intl]);


    const checkThenSubmit = useCallback(async () => {
        if (Object.keys(errors).length > 0) {
            snackbar.show(
                <div
                    role="alert"
                    aria-live="assertive"
                    aria-atomic="true"
                    tabIndex="-1"
                    aria-label={incompleteApplicationWarning.ariaLabel}
                >
                    {incompleteApplicationWarning.message}
                </div>
            );
            return false;
        }
        setisSubmitting(true);
        if (inquiryMode) {
            try {
                const { isSenderEmailVerified } = await api.communicate.getVerifiedEmails(senderEmail);

                if (!isSenderEmailVerified) {
                    snackbar.error(intl.formatMessage({ id: 'adminGeneralErrorMessage' }));
                    return;
                } else {
                    const applicationPage = matchPath(history.location.pathname, {
                        path: [
                            routes.externalApplicationPageById
                        ]
                    });
                    const pageId = applicationPage ? applicationPage.params.pageId : null;
                    await dispatch(setPageAndCloseDialogAction({ history, pageId, isAps }));
                    await submit(values);
                    let route = generatePath(routes.applicationFormSubmitPage, { id: applicationId });
                    if (previewMode) {
                        route = generatePath(routes.importsTemplatePreviewSubmitPage, { id: applicationId });
                    }
                    else if (externalMode) {
                        route = generatePath(routes.externalApplicationSubmitPage, { id: applicationId });
                    }
                    history.push(route);
                }
            } catch (e) {
                console.error(e);
                snackbar.error(intl.formatMessage({ id: 'unableToSubmitInquiry' }));
            }
            setisSubmitting(false);
            return;
        }
        if (supplementalFormMode) {
            try {
                const result = await submit(values);
                const route = routes.applicationFormSupplementalItemsPage.replace(':id', parentApplicationId);
                history.push(route, result);
            } catch (e) {
                console.error(e);
                snackbar.error(intl.formatMessage({ id: 'unableToSubmitSupplementalForm' }));
            }
            setisSubmitting(false);
            return;
        }
        setisSubmitting(false);
        setShowSubmissionConfirmation(true);
    }, [errors, inquiryMode, snackbar, submit, values]);

    const goToPayments = useCallback(() => {
        if (Object.keys(errors).length > 0) {
            snackbar.show(
                <div
                    role="alert"
                    aria-live="assertive"
                    aria-atomic="true"
                    tabIndex="-1"
                    aria-label={incompleteApplicationWarning.ariaLabel}
                >
                    {incompleteApplicationWarning.message}
                </div>
            );
            return false;
        }
        history.push(generatePath(routes.applicationFormPayments, { id: applicationId }));
    }, [history.push, errors, snackbar]);

    const handlePayClick = useCallback(() => {
        if (!inquiryMode && !supplementalFormMode) {
            if (Object.keys(errors).length > 0) {
                snackbar.show(
                    <div
                        role="alert"
                        aria-live="assertive"
                        aria-atomic="true"
                        tabIndex="-1"
                        aria-label={incompleteApplicationWarning.ariaLabel}
                    >
                        {incompleteApplicationWarning.message}
                    </div>
                );
                return false;
            }
            if (applicationPayment?.appliedDiscountValue >= applicationPayment?.amount) {
                handlePaymentConfirmationButtonClick('yes');
            } else {
                setShowPaymentConfirmation(true);
            }
        }
    }, [applicationPayment, inquiryMode, supplementalFormMode, setShowPaymentConfirmation]);

    const handlePaymentConfirmationButtonClick = useCallback(async buttonId => {
        closePaymentConfirmation();
        if (buttonId === 'yes') {
            dispatch(startLoading());
            try {
                if (applicationPayment?.paymentStatus !== paymentStatus.successful) {
                    const redirect = await getRedirectUrl();
                    if (redirect !== paymentStatus.successful) {
                        // snackbar.info(intl.formatMessage({ id: 'browserCloseWarning' }));
                        dispatch(startLoading());
                        window.location.href = redirect.epsRedirectUrl;
                    } else {
                        snackbar.success(intl.formatMessage({ id: 'paymentSuccessful' }));
                        await submitApplication();
                    }
                }
            } catch (e) {
                if (e.message === intl.formatMessage({ id: 'applicationFeeDiscountCodeInvalid' })) {
                    snackbar.error(intl.formatMessage({ id: 'applicationFeeDiscountCodeInvalid' }));
                }
                else {
                    console.log(e);
                    snackbar.error(intl.formatMessage({ id: 'paymentError' }));
                }
                console.error(e);
                dispatch(stopLoading());
            }
        }
    }, [dispatch, startLoading, stopLoading, applicationFee, globalSettings?.paymentSettings, applicationId, prospect, snackbar, applicationPayment]);

    useEffect(() => {
        const fn = async () => {
            let paymentSuccessful = false;
            try {
                const isOnPaymentsPage = matchPath(history.location.pathname, {
                    path: [
                        routes.applicationFormPayments,
                        routes.externalApplicationPayments,
                    ]
                });
                dispatch(startLoading());
                if (isOnPaymentsPage && applicationPayment) {
                    const applicationPaymentStatus = applicationPayment?.paymentStatus;
                    if ([paymentStatus.draft].includes(applicationPaymentStatus)) {
                        return;
                    }
                    dispatch(startLoading());
                    const { paymentStatus: updatedPayment, showSnackBar } = await getPaymentStatus(applicationPayment);
                    if (updatedPayment === paymentStatus.pending) {
                        snackbar.error(intl.formatMessage({ id: 'paymentError' }));
                    } else if (updatedPayment === paymentStatus.failed && showSnackBar) {
                        snackbar.error(intl.formatMessage({ id: 'paymentFailedError' }));
                    } else if (updatedPayment === paymentStatus.canceled && showSnackBar) {
                        snackbar.error(intl.formatMessage({ id: 'paymentCanceledError' }));
                    } else if (updatedPayment === paymentStatus.successful) {
                        paymentSuccessful = true;
                        snackbar.success(intl.formatMessage({ id: 'paymentSuccessful' }));
                        dispatch(startLoading());
                        await submitApplication();
                    }
                }
            } catch (e) {
                console.error(e);
            } finally {
                if (!paymentSuccessful) {
                    dispatch(stopLoading());
                }
            }
        };
        fn();
    }, [history?.location?.pathname]);

    const onContinue = useCallback(() => {
        onContinueProp(nextAvailablePageId);
    }, [onContinueProp, nextAvailablePageId]);

    useEffect(() => {
        const isOnApplicationFormPage = matchPath(history.location.pathname, {
            path: [
                routes.applicationFormPageById
            ]
        });
        const nextPageTitle = document.getElementById('page-title');
        if (nextPageTitle && isOnApplicationFormPage) {
            nextPageTitle.focus();
        }
    }, [history?.location]);

    const onBack = useCallback(() => onBackProp(previousAvailablePageId), [onBackProp, previousAvailablePageId]);
    const goToSupplementalItems = useCallback(() => {
        onPageClickProp && onPageClickProp(CONSTANTS.pageNames.supplementalItems);
    }, [onPageClickProp]);
    const goToSubmissionPage = useCallback(() => {
        onPageClickProp && onPageClickProp(CONSTANTS.pageNames.submission);
    }, [onPageClickProp]);

    return (
        <div className={classes.root}>
            <Route
                exact
                path={[
                    routes.applicationFormPageById,
                    routes.adminTemplatePreviewPageById,
                    routes.externalApplicationPageById,
                    routes.applicationFormSupplementalForm,
                    routes.importsTemplatePreviewPageById,
                    routes.importsApplicationEditPageById
                ]}>
                <>
                    <div className={classes.back}>
                        {previousAvailablePageId &&
                            <Button disabled={disableNavigation} onClick={onBack} {...backButtonProps}>
                                <FormattedMessage id="back" />
                            </Button>
                        }
                    </div>
                    <span></span>
                    <div className={classes.continue}>
                        <>
                            {nextAvailablePageId &&
                                <Button disabled={disableNavigation} onClick={onContinue} {...continueButtonProps}>
                                    <FormattedMessage id="continue" />
                                </Button>
                            }
                            {!inquiryMode && !supplementalFormMode && !nextAvailablePageId && !importAdminMode &&
                                <Button disabled={disableNavigation} onClick={onContinue} {...continueButtonProps}>
                                    <FormattedMessage id="review" />
                                </Button>
                            }
                            {(inquiryMode || supplementalFormMode) && !nextAvailablePageId && !isSubmitted && !isAps &&
                                <Button disabled={isSubmitting || previewMode || disableNavigation} onClick={checkThenSubmit} {...continueButtonProps}>
                                    <FormattedMessage id="submit" />
                                </Button>
                            }
                        </>
                    </div>
                </>
            </Route>
            <Route exact path={[
                routes.applicationFormReviewPage,
                routes.adminTemplatePreviewReviewPage,
                routes.externalApplicationwReviewPage,
                routes.importsTemplatePreviewReviewPage
            ]}>
                {!isSubmitted &&
                    <>
                        {!isPaymentPending &&
                            <div className={classes.back}>
                                <Button
                                    onClick={onBack}
                                    {...backButtonProps}
                                >
                                    <FormattedMessage id="back" />
                                </Button>
                            </div>}
                        <span></span>
                        <div className={classes.continue}>
                            {(!applicationFee || previewMode || !shouldPaymentShow) && !isAps &&
                                <Button disabled={isSubmitting || disableNavigation || rootCtx.isAutosaving} onClick={checkThenSubmit} {...continueButtonProps}>
                                    <FormattedMessage id="submit" />
                                </Button>
                            }
                            {(!!applicationFee && shouldPaymentShow) && !previewMode && !isAps &&
                                <Button disabled={disableNavigation || rootCtx.isAutosaving} onClick={goToPayments} {...continueButtonProps}>
                                    <FormattedMessage id="continueToPayment" />
                                </Button>
                            }
                        </div>
                    </>
                }
            </Route>
            <Route exact path={[
                routes.applicationFormPayments,
                routes.externalApplicationPayments,
            ]}>
                {(!isSubmitted || shouldPaymentShow) &&
                    <>
                        <span></span>
                        <span></span>
                        <div className={classes.continue}>
                            <Button disabled={disableNavigation || applicationPayment?.paymentStatus === CONSTANTS.applicationPaymentInformation.paymentStatus.pending || rootCtx.isAutosaving} onClick={handlePayClick} {...continueButtonProps}>
                                <FormattedMessage id="payButton" />
                            </Button>
                        </div>
                    </>
                }

            </Route>
            {enableSupplementalItems && !inquiryMode &&
                <Route exact path={[
                    routes.adminTemplatePreviewSubmitPage,
                    routes.applicationFormSubmitPage,
                    routes.externalApplicationSubmitPage,
                ]}>
                    <span></span>
                    <span></span>
                    <div className={classes.continue}>
                        <Button disabled={disableNavigation} onClick={goToSupplementalItems} {...continueButtonProps}>
                            <FormattedMessage id="continue" />
                        </Button>
                    </div>
                </Route>
            }
            <Route exact path={[
                routes.adminTemplatePreviewSupplementalItemsPage,
                routes.applicationFormSupplementalItemsPage,
                routes.externalApplicationSupplementalItemsPage,
            ]}>
                <div className={classes.back}>
                    <Button disabled={disableNavigation} onClick={goToSubmissionPage} {...backButtonProps}>
                        <FormattedMessage id="back" />
                    </Button>
                </div>
            </Route>
            <SimpleDialog
                buttonConfig={[
                    { id: 'yes', title: intl.formatMessage({ id: 'applicationSubmissionYes' }), },
                    { id: 'no', title: intl.formatMessage({ id: 'applicationSubmissionNo' }), },
                ]}
                closeOnClickOut={true}
                fullScreen={isMobile}
                handleButtonClick={handleSubmissionConfirmationButtonClick}
                onClose={closeSubmissionConfirmation}
                open={showSubmissionConfirmation}
                rightAlignButtons={true}
                showTitleBarClose={true}
                title={intl.formatMessage({ id: 'applicationSubmissionTitle' })}
            >
                <Typography>
                    <FormattedMessage id="applicationSubmissionText" />
                </Typography>
            </SimpleDialog>
            <SimpleDialog
                buttonConfig={[
                    { id: 'yes', title: intl.formatMessage({ id: 'yes' }), },
                    { id: 'no', title: intl.formatMessage({ id: 'no' }), },
                ]}
                closeOnClickOut={true}
                fullScreen={isMobile}
                handleButtonClick={handlePaymentConfirmationButtonClick}
                onClose={closePaymentConfirmation}
                open={showPaymentConfirmation}
                rightAlignButtons={true}
                showTitleBarClose={true}
                title={intl.formatMessage({ id: 'payConfirmationHeader' })}
            >
                <Typography>
                    <FormattedMessage id="payConfirmationContent" />
                </Typography>
            </SimpleDialog>
            {showSubmitted && <SubmittedDialog isOpen={showSubmitted} onClose={closeSubmitted} />}
        </div>
    );
};
export default ApplicationFormNavigationBar;
